/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { Accordion, AccordionHeader, AccordionPanel, Button, Grid } from '@coursera/cds-core';

import { useThirdPartyOrganization } from 'bundles/admin-dashboard/components/ThirdPartyOrganizationProvider';
import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import CourseSelectionDrawer from 'bundles/enterprise-admin-user-filters/components/CourseSelectionDrawer';
import FilterPills from 'bundles/enterprise-admin-user-filters/components/FilterPills';
import { Spacer } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type EnrolledInCoursesProps = {
  selectedCourses: FilterOption[];
  onlySpecialization?: boolean;
  onCoursesSelected: (selectedCourses: FilterOption[]) => void;
  defaultExpanded?: boolean;
};

const MAX_COURSES_DISPLAYED = 7;

export const EnrolledInCoursesAccordion = ({
  selectedCourses,
  onlySpecialization,
  defaultExpanded,
  onCoursesSelected,
}: EnrolledInCoursesProps) => {
  const [isCourseSelectionDrawerOpen, setIsCourseSelectionDrawerOpen] = React.useState<boolean>(false);
  const thirdPartyOrganization = useThirdPartyOrganization();

  const handleDismissCourse = (id: string) => {
    onCoursesSelected(selectedCourses.filter((fo) => fo.id !== id));
  };

  const handleDismissAllCourses = () => {
    onCoursesSelected([]);
  };

  const courseType = onlySpecialization ? _t('specializations') : _t('courses');

  return (
    <>
      <Accordion variant="silent" defaultExpanded={defaultExpanded}>
        <AccordionHeader
          label={_t('Enrolled in #{courseType}: #{message}', {
            message:
              selectedCourses.length > 0
                ? _t(`Selected #{courseType}`, { courseType })
                : _t(`Any #{courseType}`, { courseType }),
            courseType,
          })}
        />
        <AccordionPanel>
          <Spacer size={16} />
          {selectedCourses.length > 0 ? (
            <Grid>
              <FilterPills
                label={_t('Selected #{type}', { type: courseType })}
                filterOptions={selectedCourses}
                numFiltersDisplayed={MAX_COURSES_DISPLAYED}
                onDismiss={handleDismissCourse}
              />
              <Spacer size={32} />
              <Grid container spacing={24}>
                <Grid item>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => setIsCourseSelectionDrawerOpen(true)}
                    data-testid="enrolled-in-courses-select_filled"
                  >
                    {_t('Select #{type}', { type: courseType })}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleDismissAllCourses}
                    data-testid="enrolled-in-courses-select-clear-all"
                  >
                    {_t('Clear all selections')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Button
              variant="secondary"
              onClick={() => setIsCourseSelectionDrawerOpen(true)}
              data-testid="enrolled-in-courses-select-courses"
              size="small"
            >
              {_t('Select #{type}', { type: courseType })}
            </Button>
          )}
        </AccordionPanel>
      </Accordion>
      <CourseSelectionDrawer
        isOpen={isCourseSelectionDrawerOpen}
        thirdPartyOrganization={thirdPartyOrganization}
        selectedCourses={selectedCourses}
        onlySpecialization={onlySpecialization}
        onClose={() => setIsCourseSelectionDrawerOpen(false)}
        onApply={onCoursesSelected}
      />
    </>
  );
};
